import React from 'react'

import { Avatar } from '@wartek-id/avatar'
import { Button } from '@wartek-id/button-v1'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import {
  GURU_AI_AVATAR_IMAGE_PATH,
  NETWORK_ERROR_MESSAGE_RESPONSE_DATA,
  INTERNAL_SERVER_ERROR_MESSAGE_RESPONSE_DATA,
} from 'app/ConversationPage/constants'
import useConversation from 'app/ConversationPage/hooks/useConversation'
import { useSETrackEvent } from 'app/ConversationPage/hooks/useSETrackEvent'
import {
  useConversationStore,
  useConversationMessageStore,
} from 'stores/conversation'

import type { TConversationMessageErrorType } from 'types/conversation'

interface IConversationMessageErrorProps {
  errorType?: TConversationMessageErrorType
  isLoading?: boolean
  isLastItem?: boolean
}
const ConversationMessageError: React.FC<IConversationMessageErrorProps> = ({
  errorType,
  isLoading,
  isLastItem,
}) => {
  const { retrySendMessage } = useConversation()
  const { conversationId } = useConversationStore()
  const { setConfirmSheetOpen } = useConversationMessageStore()
  const { SETrackConversationPage } = useSETrackEvent()

  React.useEffect(() => {
    if (errorType === 'network_error') {
      SETrackConversationPage('ERROR_NO_INTERNET', {
        ai_chat_session_id: conversationId || null,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorType])

  if (!Boolean(errorType)) {
    return null
  }

  return (
    <section className="bot" role="region">
      <div className="p-4 bg-white">
        <div className="flex items-start space-x-4">
          <div className="flex-none">
            <Avatar
              src={GURU_AI_AVATAR_IMAGE_PATH}
              name="Asisten Guru"
              size="md"
              alt="Bot avatar"
            />
          </div>
          <div data-testid="conversation-message-error-content">
            {isLastItem && isLoading ? (
              <div
                data-testid="conversation-message-error-loading"
                className="!pt-2"
              >
                <Spinner size="sm" />
              </div>
            ) : (
              <>
                {errorType === 'idle_session_expired' && (
                  <div className="space-y-6">
                    <Text>
                      Maaf... Harap mulai diskusi baru, ya.
                      <br />
                      Waktu diskusi yang ini sudah habis.
                      <br />
                      <br />
                      Pastikan Anda sudah salin jawaban Asisten Guru karena Anda
                      tidak bisa akses jawaban ini lagi.
                    </Text>
                    <Button
                      onClick={() => {
                        setConfirmSheetOpen(true)
                      }}
                    >
                      Mulai diskusi baru
                    </Button>
                  </div>
                )}

                {errorType === 'memory_limit_exceeded' && (
                  <div className="space-y-6">
                    <Text>
                      Maaf... Harap mulai diskusi baru, ya.
                      <br />
                      <br />
                      Anda sudah mencapai batas panjang diskusi.
                      <br />
                      <br />
                      Pastikan Anda sudah salin jawaban Asisten Guru karena Anda
                      tidak bisa akses jawaban ini lagi.
                    </Text>
                    <Button
                      onClick={() => {
                        setConfirmSheetOpen(true)
                      }}
                    >
                      Mulai diskusi baru
                    </Button>
                  </div>
                )}

                {errorType === 'daily_quota_exceeded' && (
                  <Text>
                    Anda mencapai batas diskusi harian. Silakan mulai diskusi
                    lagi besok, ya.
                  </Text>
                )}

                {errorType === 'global_token_limit_reached' && (
                  <Text>
                    Maaf, kuota akses Asisten Guru penuh.
                    <br />
                    <br />
                    Saat ini kuota akses Asisten Guru terbatas karena masih uji
                    coba.
                    <br />
                    <br />
                    Coba lagi besok, ya.
                  </Text>
                )}

                {errorType === 'network_error' && (
                  <div className="space-y-6">
                    <Text color="critical">
                      {NETWORK_ERROR_MESSAGE_RESPONSE_DATA.message}
                    </Text>
                    <Button onClick={retrySendMessage}>Coba lagi</Button>
                  </div>
                )}

                {(errorType === 'internal_server_error' ||
                  errorType === 'bad_request_error') && (
                  <div className="space-y-6">
                    <Text color="critical">
                      {INTERNAL_SERVER_ERROR_MESSAGE_RESPONSE_DATA.message}
                    </Text>
                    {errorType === 'bad_request_error' ? (
                      <Button
                        onClick={() => {
                          setConfirmSheetOpen(true)
                        }}
                      >
                        Mulai diskusi baru
                      </Button>
                    ) : (
                      <Button onClick={retrySendMessage}>Coba lagi</Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ConversationMessageError
